
import { useLocation } from "react-router-dom"
import { Settings, LogOut, ChevronRight, Warehouse, Building2, Check, Factory } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useAuth, WarehouseContext, FacilityContext } from "@/contexts/auth-context"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu"
import { Link } from "react-router-dom"
import { LanguageSwitcher } from "@/components/language-switcher"
import { useTranslations } from "@/i18n"
import { usePageHeaderSlot } from "@/contexts/page-header-slot-context"
import { SidebarTrigger } from "@/components/ui/sidebar"

const pageKeys: Record<string, string> = {
  "/": "dashboard",
  "/dashboard": "dashboard",
  "/inventory": "inventory",
  "/master-list": "products",
  "/inbound-receiving": "inbound",
  "/putaway": "putaway",
  "/sales-orders": "salesOrders",
  "/outbound-shipments": "outbound",
  "/stocktake": "stocktake",
  "/inventory/locations": "locations",
  "/inventory/missing": "missingItems",
  "/inventory/disposal": "disposal",
  "/inventory/relocation": "relocation",
  "/inventory/expiry": "expiry",
  "/warehouse-tasks": "warehouseTasks",
  "/settings/locations": "settingsLocations",
  "/settings/general": "generalSettings",
  "/settings/dispatch": "settingsDispatch",
  "/settings/products/attributes": "productAttributes",
  "/suppliers": "suppliers",
  "/customers": "customers",
  "/users": "users",
  "/nfc-cards": "nfcCards",
  "/settings": "settings",
  "/print-queue": "printQueue",
  "/facilities": "facilities",
  "/reports/inbound": "reportsInbound",
  "/reports/outbound": "reportsOutbound",
  "/reports/inventory": "reportsInventory",
  "/reports/movement": "reportsMovement",
  "/reports/stocktake": "reportsStocktake",
  "/admin": "admin",
}

function getInitials(name?: string | null, email?: string | null): string {
  if (name) {
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.slice(0, 2).toUpperCase()
  }
  if (email) {
    return email.slice(0, 2).toUpperCase()
  }
  return 'U'
}

export function Header() {
  const pathname = useLocation().pathname
  const {
    user,
    logout,
    isLoading,
    // Facility context
    activeFacility,
    availableFacilities,
    setActiveFacility,
    // Warehouse context
    activeWarehouse,
    availableWarehouses,
    filteredWarehouses,
    setActiveWarehouse,
  } = useAuth()
  const t = useTranslations('pages')
  const tCommon = useTranslations('common')
  const tAuth = useTranslations('auth')
  const { setSlotElement, active: slotActive } = usePageHeaderSlot()

  // Don't show WMS header for system admins
  if (!isLoading && user?.isSystemAdmin) {
    return null
  }

  const pageKey = pageKeys[pathname] || "dashboard"
  const pageInfo = { title: t(`${pageKey}.title`), description: t(`${pageKey}.description`) }

  // Determine visibility of selectors based on user access
  const canSwitch = user?.canSwitchWarehouse
  const hasMultipleFacilities = availableFacilities.length > 1
  const hasMultipleWarehousesTotal = availableWarehouses.length > 1
  
  // Hide selectors entirely if user can't switch or has only one option
  const showFacilitySelector = canSwitch && hasMultipleFacilities
  // Show warehouse selector if user has multiple warehouses total (allows switching across facilities)
  const showWarehouseSelector = canSwitch && hasMultipleWarehousesTotal

  return (
    <header className="sticky top-0 z-30 bg-sidebar">
      <div className="flex items-center justify-between px-6 py-4 gap-2">
        {/* Trigger + page title slot (page title is replaced by <PageHeaderSlot> contents when a page injects one) */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <SidebarTrigger className="text-muted-foreground hover:text-foreground" />
          <div className="min-w-0 flex-1">
            <div ref={setSlotElement} />
            {!slotActive && (
              <>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-semibold text-foreground tracking-tight">{pageInfo.title}</h1>
                </div>
                {pageInfo.description && (
                  <p className="text-sm text-muted-foreground mt-0.5">{pageInfo.description}</p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5">
          {/* Static warehouse indicator for locked users (can't switch) */}
          {user && !user.isSystemAdmin && !canSwitch && activeWarehouse && (
            <>
              <div className="flex items-center gap-1.5 px-2 py-1 text-sm text-muted-foreground">
                <Warehouse className="h-4 w-4" />
                <span className="font-medium">{activeWarehouse.warehouseCode}</span>
                {activeFacility && (
                  <span className="text-xs opacity-60">({activeFacility.facilityCode})</span>
                )}
              </div>
              <div className="h-5 w-px bg-border/40 mx-1.5" />
            </>
          )}

          {/* Facility Selector - Only show if user can switch between multiple facilities */}
          {user && !user.isSystemAdmin && showFacilitySelector && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-1.5 px-2.5 text-muted-foreground hover:text-foreground hover:bg-muted/50"
                >
                  <Factory className="h-4 w-4" />
                  <span className="text-sm font-medium max-w-[100px] truncate">
                    {activeFacility ? activeFacility.facilityCode : 'All'}
                  </span>
                  <ChevronRight className="h-3 w-3 rotate-90 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                  {tCommon('selectFacility')}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                {/* All Facilities option - only for admins with full access */}
                {!user?.defaultWarehouse && (
                  <DropdownMenuItem 
                    onClick={() => setActiveFacility(null)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span>{tCommon('allFacilities')}</span>
                    </div>
                    {!activeFacility && <Check className="h-4 w-4 text-primary" />}
                  </DropdownMenuItem>
                )}
                
                {availableFacilities.map((facility) => (
                  <DropdownMenuItem 
                    key={facility.facilityId}
                    onClick={() => setActiveFacility(facility)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <Factory className="h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col">
                        <span className="text-sm">{facility.facilityCode}</span>
                        <span className="text-xs text-muted-foreground">{facility.facilityName}</span>
                      </div>
                    </div>
                    {activeFacility?.facilityId === facility.facilityId && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Warehouse Selector - Only show if user can switch between multiple warehouses */}
          {user && !user.isSystemAdmin && showWarehouseSelector && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-1.5 px-2.5 text-muted-foreground hover:text-foreground hover:bg-muted/50"
                >
                  <Warehouse className="h-4 w-4" />
                  <span className="text-sm font-medium max-w-[100px] truncate">
                    {activeWarehouse ? activeWarehouse.warehouseCode : 'All'}
                  </span>
                  <ChevronRight className="h-3 w-3 rotate-90 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto">
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                  {activeFacility
                    ? tCommon('warehousesIn', { facility: activeFacility.facilityCode })
                    : tCommon('allWarehouses')}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                {/* All Warehouses in Facility - only if facility is selected */}
                {activeFacility && !user?.defaultWarehouse && filteredWarehouses.length > 1 && (
                  <DropdownMenuItem 
                    onClick={() => setActiveWarehouse(null)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span>{tCommon('allIn', { facility: activeFacility.facilityCode })}</span>
                    </div>
                    {!activeWarehouse && <Check className="h-4 w-4 text-primary" />}
                  </DropdownMenuItem>
                )}
                
                {/* All Warehouses option - only for admins with no facility filter */}
                {!activeFacility && !user?.defaultWarehouse && (
                  <DropdownMenuItem 
                    onClick={() => setActiveWarehouse(null)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span>{tCommon('allWarehouses')}</span>
                    </div>
                    {!activeWarehouse && <Check className="h-4 w-4 text-primary" />}
                  </DropdownMenuItem>
                )}
                
                {filteredWarehouses.map((wh) => (
                  <DropdownMenuItem 
                    key={wh.warehouseId}
                    onClick={() => setActiveWarehouse(wh)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <Warehouse className="h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col">
                        <span className="text-sm">{wh.warehouseCode}</span>
                        <span className="text-xs text-muted-foreground">{wh.warehouseName}</span>
                      </div>
                    </div>
                    {activeWarehouse?.warehouseId === wh.warehouseId && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Separator after selectors - only if at least one selector is shown */}
          {user && !user.isSystemAdmin && (showFacilitySelector || showWarehouseSelector) && (
            <div className="h-5 w-px bg-border/40 mx-1" />
          )}

          {/* Language Switcher */}
          <LanguageSwitcher variant="minimal" />

          {/* Settings Link */}
          <Link to="/settings">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full cursor-pointer text-muted-foreground hover:text-foreground hover:bg-muted/50"
            >
              <Settings className="h-4 w-4" />
              <span className="sr-only">Settings</span>
            </Button>
          </Link>

          {/* Separator */}
          <div className="h-5 w-px bg-border/40 mx-1" />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 gap-2 pl-1.5 pr-2.5 rounded-full hover:bg-muted/50"
              >
                <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-[11px] font-medium text-primary">
                    {getInitials(user?.fullName, user?.email)}
                  </span>
                </div>
                <span className="text-sm font-medium text-foreground hidden sm:inline-block max-w-[100px] truncate">
                  {user?.fullName || user?.email?.split('@')[0]}
                </span>
                <ChevronRight className="h-3 w-3 rotate-90 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-medium text-primary">
                      {getInitials(user?.fullName, user?.email)}
                    </span>
                  </div>
                  <div className="flex flex-col min-w-0">
                    <p className="text-sm font-medium truncate">{user?.fullName || 'User'}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={logout} 
                className="text-destructive focus:text-destructive cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {tAuth('logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}
